import type {ReactElement} from 'react';
import React from 'react';
import {PosterEditingBaseDropdownOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-dropdown-option';
import {BaseItemCustomShadowOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-custom-shadow-opacity-option';
import {BaseItemCustomShadowColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-custom-shadow-color-option';
import {BaseItemCustomShadowAngleOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-custom-shadow-angle-option';
import {BaseItemCustomShadowDistanceOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-custom-shadow-distance-option';
import {BaseItemCustomShadowBlurOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-custom-shadow-blur-option';
import type {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {ShadowOrGlowOptionsObject} from '@Utils/fabric-shadow.util';
import {ShadowType} from '@PosterWhiteboard/poster/poster-backend.types';
import {ShadowOrGlowType} from '@postermywall/fabricjs-2';

interface BaseItemShadowOptionProps {
  shadowOptions: ShadowOrGlowOptionsObject | null;
  itemType: ITEM_TYPE;

  onClickColorFill(): void;

  onToggleShadow(newShadowType: ShadowType | null): void;

  onUpdateOpacity(dropShadowAlpha: number, undoable: boolean): void;

  onUpdateAngle(dropShadowAngle: number, undoable: boolean): void;

  onUpdateDistance(dropShadowDistance: number, undoable: boolean): void;

  onUpdateBlur(dropShadowBlur: number, undoable: boolean): void;
}

export function BaseItemShadowOptionsWeb({...props}: BaseItemShadowOptionProps): ReactElement<any> | null {
  const getSelectedShadowType = (): ShadowType => {
    if (!props.shadowOptions) {
      return ShadowType.NONE;
    }

    switch (props.shadowOptions.shadowOrGlowType) {
      case ShadowOrGlowType.LIGHT_GLOW:
      case ShadowOrGlowType.STRONG_GLOW:
      case null:
        return ShadowType.NONE;
      case ShadowOrGlowType.CUSTOM_SHADOW:
        return ShadowType.CUSTOM;
      case ShadowOrGlowType.STRONG_SHADOW:
        return ShadowType.STRONG;
      case ShadowOrGlowType.LIGHT_SHADOW:
        return ShadowType.LIGHT;
      default:
        throw new Error(`Unhandled shadowOrGlowType type: ${props.shadowOptions.shadowOrGlowType} in BaseItemShadowOption`);
    }
  };

  const getShadowText = (text: ShadowType): string => {
    switch (text) {
      case ShadowType.CUSTOM:
        return window.i18next.t('pmwjs_custom_shadow');
      case ShadowType.LIGHT:
        return window.i18next.t('pmwjs_light_shadow');
      case ShadowType.NONE:
        return window.i18next.t('pmwjs_none');
      case ShadowType.STRONG:
        return window.i18next.t('pmwjs_strong_shadow');
      default:
        throw new Error('Unhandled shadow option');
    }
  };

  const onClickHandler = (clickedId: ShadowType): void => {
    if (clickedId === getSelectedShadowType()) {
      return;
    }

    props.onToggleShadow(clickedId);
  };

  const updateOpacity = (dropShadowAlpha: number, undoable = true): void => {
    props.onUpdateOpacity(dropShadowAlpha, undoable);
  };

  const updateAngle = (dropShadowAngle: number, undoable = true): void => {
    props.onUpdateAngle(dropShadowAngle, undoable);
  };

  const updateDistance = (dropShadowDistance: number, undoable = true): void => {
    props.onUpdateDistance(dropShadowDistance, undoable);
  };

  const updateBlur = (dropShadowBlur: number, undoable = true): void => {
    props.onUpdateBlur(dropShadowBlur, undoable);
  };

  const getColorSubOption = (): ReactElement<any> | null => {
    if (!props.shadowOptions) {
      return null;
    }

    return (
      <BaseItemCustomShadowColorOption
        dropShadowColor={props.shadowOptions.color}
        onClickColorFill={(): void => {
          props.onClickColorFill();
        }}
      />
    );
  };

  const getOpacitySubOption = (): ReactElement<any> | null => {
    if (!props.shadowOptions) {
      return null;
    }

    return <BaseItemCustomShadowOpacityOption dropShadowAlpha={props.shadowOptions.color[3]} onUpdateOpacity={updateOpacity} />;
  };

  const getDistanceSubOption = (): ReactElement<any> | null => {
    if (!props.shadowOptions) {
      return null;
    }

    return <BaseItemCustomShadowDistanceOption dropShadowDistance={props.shadowOptions.distance} onUpdateDistance={updateDistance} />;
  };

  const getBlurSubOption = (): ReactElement<any> | null => {
    if (!props.shadowOptions) {
      return null;
    }

    return <BaseItemCustomShadowBlurOption dropShadowBlur={props.shadowOptions.blur} onUpdateBlur={updateBlur} />;
  };

  const getAngleSubOption = (): ReactElement<any> | null => {
    if (!props.shadowOptions) {
      return null;
    }

    return <BaseItemCustomShadowAngleOption dropShadowAngle={props.shadowOptions.angle} onUpdateAngle={updateAngle} />;
  };

  const getSubOptions = (): ReactElement<any> | null => {
    if (getSelectedShadowType() === ShadowType.STRONG || getSelectedShadowType() === ShadowType.LIGHT) {
      return <>{getDistanceSubOption()}</>;
    }

    if (getSelectedShadowType() === ShadowType.CUSTOM) {
      return (
        <>
          {getColorSubOption()}
          {getOpacitySubOption()}
          {getDistanceSubOption()}
          {getAngleSubOption()}
          {getBlurSubOption()}
        </>
      );
    }
    return null;
  };

  return (
    <PosterEditingBaseDropdownOption
      id="base-item-shadow-option"
      label={window.i18next.t('pmwjs_shadow')}
      onClick={onClickHandler}
      dropdownItems={[
        {
          id: ShadowType.NONE,
          text: getShadowText(ShadowType.NONE),
        },
        {
          id: ShadowType.LIGHT,
          text: getShadowText(ShadowType.LIGHT),
        },
        {
          id: ShadowType.STRONG,
          text: getShadowText(ShadowType.STRONG),
        },
        {
          id: ShadowType.CUSTOM,
          text: getShadowText(ShadowType.CUSTOM),
        },
      ]}
      selectedItemId={getSelectedShadowType()}
    >
      {getSubOptions()}
    </PosterEditingBaseDropdownOption>
  );
}
